// ═══════════════════════════════════════════════════════════════
// pointTree.ts – bejegyzések alpontok szerinti csoportosítása
//
// „2.4 › Adóigazgatás” (vagy a régi „2.3 › Adóigazgatás › Tétel”) → szülő: 2.4,
// csoport: Adóigazgatás. A szülőpont saját bejegyzése a csoportok előtt áll; ha nincs
// ilyen, egy fejléc (rec: null) jelzi. A sorrend a bemenet sorrendjét követi.
// ═══════════════════════════════════════════════════════════════

export const normPontKey = (p: unknown) => String(p ?? '').trim().replace(/\.+$/, '');

export interface SubPoint { parentKey: string; parentLabel: string; label: string; key: string }

export function subPointOfData(d: Record<string, any> | null | undefined): SubPoint | null {
  const p = String(d?.pont ?? '').trim();
  if (!p.includes('›')) return null;
  const parts = p.split('›').map((x) => x.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  const rest = parts.slice(1);
  const title = String(d?.cim ?? d?.megnevezes ?? '').trim().toLowerCase();
  if (rest.length > 1 && rest[rest.length - 1].toLowerCase() === title) rest.pop();
  const label = rest.join(' › ') || 'Egyéb';
  const parentKey = normPontKey(parts[0]);
  return { parentKey, parentLabel: parts[0], label, key: `${parentKey}|${label}` };
}

export interface PointGroup<T> { key: string; label: string; items: T[] }
export interface PointNode<T> { rec: T | null; key: string; label: string; groups: PointGroup<T>[] }

export function buildPointTree<T>(items: T[], getData: (r: T) => Record<string, any>, getId: (r: T) => string | number): PointNode<T>[] {
  const parents = new Map<string, PointNode<T>>();
  for (const rec of items) {
    const d = getData(rec);
    if (subPointOfData(d)) continue;
    const k = normPontKey(d?.pont);
    if (k && !parents.has(k)) parents.set(k, { rec, key: k, label: String(d?.pont ?? ''), groups: [] });
  }
  const out: PointNode<T>[] = [];
  const placed = new Set<PointNode<T>>();
  for (const rec of items) {
    const d = getData(rec);
    const sub = subPointOfData(d);
    if (!sub) {
      const k = normPontKey(d?.pont);
      const n = k ? parents.get(k) : undefined;
      if (n && n.rec === rec) { out.push(n); placed.add(n); }
      else out.push({ rec, key: `r${getId(rec)}`, label: String(d?.pont ?? ''), groups: [] });
      continue;
    }
    let n = parents.get(sub.parentKey);
    if (!n) {
      n = { rec: null, key: sub.parentKey, label: sub.parentLabel, groups: [] };
      parents.set(sub.parentKey, n);
    }
    if (!placed.has(n) && !n.rec) { out.push(n); placed.add(n); }
    let g = n.groups.find((x) => x.label === sub.label);
    if (!g) { g = { key: sub.key, label: sub.label, items: [] }; n.groups.push(g); }
    g.items.push(rec);
  }
  return out;
}
